import {
  getShortTimeWindowLabel,
  getTimeWindowFieldLabelText,
} from "src/components/features/medium/AssetsFilter/components/TimeWindow/TimeWindow.utils";
import { DEFAULT_TAB_SEARCH_PARAM } from "src/components/general/Tabs/Tabs.constants";
import { COMPARE_RUNS_CONTEXT_LABELS } from "../../AiToolboxSection.constants";
import {
  getCompareRunsContextValue,
  getContextLabels,
  getPageType,
} from "../../AiToolboxSection.utils";

export interface ContextEntry {
  key: string;
  label: string;
  value: string;
}

/** Shorter labels for compact display vs full context labels */
const DISPLAY_LABEL_OVERRIDES: Record<string, string> = {
  timeWindow: "Time",
};

type IdNameMap = Map<number, string | undefined>;

/**
 * Resolves a context parameter to its display string.
 * Dispatches to time-window formatting, array→name resolution, or scalar conversion.
 */
function resolveParamValue(
  key: string,
  raw: string | number | boolean | number[] | undefined,
  arrayResolvers: Record<string, IdNameMap>
  // tzStr: string
): string | undefined {
  if (key === "timeWindow") {
    return getTimeWindowFieldLabelText(
      raw as number | string,
      undefined,
      getShortTimeWindowLabel
    );
  }

  const resolver = arrayResolvers[key];
  if (resolver && Array.isArray(raw)) {
    const names = raw.map((id) => resolver.get(id)).filter(Boolean);
    return names.length > 0 ? names.join(", ") : undefined;
  }

  return raw != null && !Array.isArray(raw) ? String(raw) : undefined;
}

/**
 * Parses the current URL state and filter params into a list of context entries
 * that represent what the AI chat will include as base context.
 */
export function parseUrlAsChatContext({
  pathname,
  searchParams,
  commonParams,
  tagNamesById,
  userNamesById,
  tzStr,
}: {
  pathname: string;
  searchParams: URLSearchParams;
  commonParams: Record<string, string | number | boolean | number[] | undefined>;
  tagNamesById: IdNameMap;
  userNamesById: IdNameMap;
  tzStr: string | undefined;
}): ContextEntry[] {
  const pageType = getPageType(pathname);

  if (pageType === "Compare Runs") {
    return (
      Object.entries(COMPARE_RUNS_CONTEXT_LABELS) as [
        keyof typeof COMPARE_RUNS_CONTEXT_LABELS,
        string,
      ][]
    )
      .map(([key, label]) => ({
        key,
        label,
        value: getCompareRunsContextValue(key, pageType, searchParams) ?? "",
      }))
      .filter((e) => e.value);
  }

  const labels = getContextLabels(pageType === "Cluster");
  const tabValue = searchParams.get(DEFAULT_TAB_SEARCH_PARAM);
  const contextParams: Record<
    string,
    string | number | boolean | number[] | undefined
  > = {
    page: pageType ?? undefined,
    ...commonParams,
    ...(tabValue && { tab: tabValue }),
  };

  const arrayResolvers: Record<string, IdNameMap> = {
    appTags: tagNamesById,
    appOwners: userNamesById,
  };

  return (Object.entries(labels) as [string, string][])
    .map(([key, label]) => ({
      key,
      label: DISPLAY_LABEL_OVERRIDES[key] ?? label,
      value:
        resolveParamValue(key, contextParams[key], arrayResolvers, tzStr ?? "") ??
        "",
    }))
    .filter((e) => e.value);
}

/** Reverse map: display label → internal key (derived from all label sources) */
const LABEL_TO_KEY: Record<string, string> = Object.fromEntries([
  ...Object.entries(getContextLabels(false)).map(([k, v]) => [v, k]),
  ...Object.entries(getContextLabels(true)).map(([k, v]) => [v, k]),
  ...Object.entries(COMPARE_RUNS_CONTEXT_LABELS).map(([k, v]) => [v, k]),
]);

function labelToKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

/**
 * Parses the prompt context string (produced by buildContextLines + formatAiContextForPrompt)
 * back into structured ContextEntry[].
 * Handles both `Label = "value"` and `Key: value` line formats.
 */
export function parsePromptContext(context: string | undefined): ContextEntry[] {
  if (!context) return [];
  const entries: ContextEntry[] = [];

  for (const line of context.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    // buildContextLines format: Label = "value"
    const eqMatch = trimmed.match(/^(.+?)\s*=\s*"(.*)"$/);
    if (eqMatch) {
      const label = eqMatch[1].trim();
      entries.push({
        key: LABEL_TO_KEY[label] ?? labelToKey(label),
        label,
        value: eqMatch[2],
      });
      continue;
    }

    // Dynamic context format: Key: value (\s+ intentional — guards against URL-like colons e.g. hdfs://)
    const colonMatch = trimmed.match(/^([^:]+):\s+(.+)$/);
    if (colonMatch) {
      const label = colonMatch[1].trim();
      entries.push({
        key: LABEL_TO_KEY[label] ?? labelToKey(label),
        label,
        value: colonMatch[2].trim(),
      });
    }
  }

  return entries;
}
